import React, { FC, useContext, useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { IAssignLicensePayload } from '../../hooks/useUsersApi';
import { LicenseExpiry } from '../LicenseExpiry/LicenseExpiry';
import styled from '@mui/styles/styled';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import { Chip } from '@mui/material';
import { compareBool, compareString, sortItems } from '../../helpers/compare';
import { UnassignedLicensesFetcher } from '../../fetchers/UnassignedLicensesFetcher';
import { ILicense } from '../../models/ILicense';

interface IProps {
  onFormPayloadUpdate: (payload: IAssignLicensePayload[]) => void;
}

interface ILicenseData extends ILicense {
  amount: number;
  totalAmount: number;
  items: ILicense[];
}

const prepareLicenseData = (licenses: ILicense[]) => {
  return sortItems(
    licenses.reduce((out, license) => {
      // Filter out expired licenses + suspended licenses
      if ((license.expiresAt && license.expiresAt < Date.now() / 1000) || license.suspended) {
        return out;
      }

      const dup = out.find(
        (another) =>
          another.name === license.name &&
          another.planType === license.planType &&
          another.expiresAt === license.expiresAt
      );

      if (dup) {
        dup.totalAmount += 1;
        dup.items.push(license);
        return out;
      } else {
        return [
          ...out,
          {
            ...license,
            totalAmount: 1,
            amount: 0,
            items: [license],
          },
        ];
      }
    }, [] as ILicenseData[]),
    [(item) => item.isAddon, (item) => item.name],
    [compareBool, compareString]
  );
};

const QuantityCell = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const Quantity = styled('span')({ flexBasis: 40 });

const QuantityModifier = styled('span')(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 26,
  width: 38,
  cursor: 'pointer',
  '&.Mui-disabled': {
    cursor: 'default',
    opacity: 0.5,
    pointerEvents: 'none',
  },
}));
const Plus = styled(Add)({ fontSize: 18 });
const Minus = styled(Remove)({ fontSize: 18 });

export const AssignLicenses: FC<IProps> = ({ onFormPayloadUpdate }) => {
  const { data: availableLicenses } = useContext(UnassignedLicensesFetcher.Context);

  const { t } = useTranslation();
  const [licenseData, setLicenseData] = useState(prepareLicenseData(availableLicenses ?? []));

  useEffect(() => {
    setLicenseData(prepareLicenseData(availableLicenses ?? []));
  }, [availableLicenses]);

  const getQuantityUpdater = (license: ILicenseData, diff: number) => () => {
    const index = licenseData.indexOf(license);
    setLicenseData([
      ...licenseData.slice(0, index),
      {
        ...licenseData[index],
        amount: licenseData[index].amount + diff,
      },
      ...licenseData.slice(index + 1),
    ]);
  };

  useEffect(() => {
    const payload: IAssignLicensePayload[] = [];

    licenseData.forEach((group) => {
      if (group.amount > 0) {
        // Get the specific licenses to be assigned from this group
        const licensesToAssign = group.items.slice(0, group.amount);

        // Group these specific licenses by customerProductId and packageId to satisfy API requirements
        const countMap = new Map<string, { count: number; license: ILicense }>();

        licensesToAssign.forEach((l) => {
          const key = `${l.customerProductId}-${l.packageId}`;
          const current = countMap.get(key);
          if (current) {
            current.count++;
          } else {
            countMap.set(key, { count: 1, license: l });
          }
        });

        countMap.forEach(({ count, license }) => {
          payload.push({
            packages: {
              amount: count,
              customerProductId: license.customerProductId,
              packageId: license.packageId,
            },
          });
        });
      }
    });

    onFormPayloadUpdate(payload);
  }, [licenseData, onFormPayloadUpdate]);

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t('assignLicenses.table.licenseHeader')}</TableCell>
          <TableCell>{t('assignLicenses.table.expiresHeader')}</TableCell>
          <TableCell>{t('assignLicenses.table.availableHeader')}</TableCell>
          <TableCell>{t('assignLicenses.table.addHeader')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {licenseData.map((license) => {
          return (
            <TableRow key={`${license.customerProductId}/${license.packageId}`}>
              <TableCell>
                {license.name} {license.isAddon ? <Chip color="info" label={t('assignLicenses.addonChip')} /> : null}
              </TableCell>
              <TableCell>
                <LicenseExpiry expiry={license.expiresAt || license.updatesExpiresAt} type={license.planType} />
              </TableCell>
              <TableCell>{license.totalAmount}</TableCell>
              <TableCell>
                <QuantityCell>
                  <Quantity>{license.amount}</Quantity>
                  <QuantityModifier
                    onClick={getQuantityUpdater(license, -1)}
                    className={license.amount <= 0 ? 'Mui-disabled' : void 0}
                  >
                    <Minus />
                  </QuantityModifier>
                  <QuantityModifier
                    onClick={getQuantityUpdater(license, 1)}
                    className={license.amount >= license.totalAmount ? 'Mui-disabled' : void 0}
                  >
                    <Plus />
                  </QuantityModifier>
                </QuantityCell>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
